import { HttpCacheRecord } from "./http-cache-record";

export class HttpCache {
  public readonly cacheName;

  constructor(cacheName: string) {
    this.cacheName = cacheName;
  }

  async match(request: Request): Promise<Response | null> {
    const record = await this.findMatching(request);
    if (!record) return null;

    if (record.satisfiesWithoutRevalidation(request)) {
      return record.response;
    } else {
      await this.pruneMatching(request);
      return null;
    }
  }

  async put(request: Request, response: Response): Promise<boolean> {
    const record = HttpCacheRecord.build(request, response);

    if (!record.storable()) {
      return false;
    }

    await this.store(request, record);

    return true;
  }

  private async store(request: Request, record: HttpCacheRecord) {
    const cache = await this.getCache();
    await cache.put(request, record.response.clone());
    await cache.put(
      this._policyMetadataRequest(request),
      new Response(JSON.stringify(record.policy.toObject())),
    );
  }

  private async findMatching(request: Request): Promise<HttpCacheRecord | null> {
    const cache = await this.getCache();
    const response = await cache.match(request);
    const policyResponse = await cache.match(this._policyMetadataRequest(request));

    if (!response || !policyResponse) return null;

    const policyObject = await policyResponse.json();
    return HttpCacheRecord.fromObject(response, policyObject);
  }

  private async pruneMatching(request: Request): Promise<void> {
    const cache = await this.getCache();
    await cache.delete(request);
    await cache.delete(this._policyMetadataRequest(request));
  }

  private _policyMetadataRequest(request: Request): Request {
    return new Request(`${request.url}:metadata`);
  }

  private async getCache() {
    return await window.caches.open(this.cacheName);
  }
}
